import clsx from "clsx";
import React from "react";
import {
  Box,
  BoxProps,
  TextField,
  Typography,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Collapse,
  IconButton,
  Skeleton,
  ListItemButton,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { uniq } from "lodash";

import { ReactComponent as SearchIcon } from "../../icons/Search.svg";
import { ReactComponent as CaretDownIcon } from "../../icons/caret-down.svg";
import { ReactComponent as RemoveIcon } from "../../icons/Bin.svg";
import { queryMatchesContent } from "../../utils/search";

import { useToastAlert } from "../app/ToastAlert/ToastAlertProvider";
import {
  ComponentTemplateBriefDto,
  LibraryPrivacy,
} from "@growth-machine-llc/stridist-api-client";
import { useDeleteComponentTemplateMutation } from "./hooks/useDeleteComponentTemplate";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { TemplatesCategoryList } from "./TemplatesCategoryList";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.selected.light,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },

  search: {
    padding: theme.spacing(3, 3, 0, 3),
    "& > div": {
      borderRadius: 0,
    },
  },

  searchInput: {
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.paper,
  },

  body: {
    padding: theme.spacing(0, 3),
    overflowY: "auto",
  },

  title: {
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 0.75,
    margin: theme.spacing(4, 0, 1),
  },

  text: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  category: {
    margin: theme.spacing(0, -2),
    borderRadius: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: "transparent",
    },
  },

  myTemplates: {
    margin: theme.spacing(0, -1),

    "& $template": {
      paddingRight: theme.spacing(5),
    },
  },

  template: {
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1),
    "&:hover": {
      backgroundColor: `${theme.palette.quote}66`,
    },
  },

  templateInCategory: {
    marginLeft: theme.spacing(2),
  },

  caret: {
    color: theme.palette.text.secondary,
    width: 16,
    height: 16,
    flexShrink: 0,
    marginRight: theme.spacing(1),
    "&:not($caretOpen)": {
      transform: "rotate(-90deg)",
    },
  },

  caretOpen: {},

  removeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    padding: theme.spacing(1),
    color: theme.palette.text.secondary,
    display: "none",

    "& svg": {
      width: theme.spacing(2),
      height: theme.spacing(2),
    },

    "$template:hover &": {
      display: "block",
    },
  },
}));

export interface ComponentTemplateLibraryProps
  extends Omit<BoxProps, "onSelect"> {
  templates: ComponentTemplateBriefDto[];
  onSelect?: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    templateId: string,
  ) => void;
  isLoadingTemplatesList: boolean;
}

export const SkeletonList = ({ count = 3 }) => {
  return (
    <List component="nav">
      {Array.from({ length: count }).map((_, index) => (
        <ListItem key={index}>
          <ListItemText>
            <Skeleton variant="text" width="60%" />
          </ListItemText>
        </ListItem>
      ))}
    </List>
  );
};

export function ComponentTemplateLibrary(props: ComponentTemplateLibraryProps) {
  const { className, templates, onSelect, isLoadingTemplatesList, ...other } =
    props;
  const s = useStyles();
  const [search, setSearch] = React.useState("");
  const [expanded, setExpanded] = React.useState<string[]>([]);
  const user = useCurrentUser();

  const { mutate: removeTemplate } = useDeleteComponentTemplateMutation();
  const { showToastAlert } = useToastAlert();
  const { privateTemplates, publicTemplates, teamTemplates } =
    React.useMemo(() => {
      const filtered = templates?.filter(({ title }) =>
        queryMatchesContent(title, search),
      );

      const privateTemplates = filtered?.filter(
        (it) =>
          it.privacy === LibraryPrivacy.PRIVATE ||
          (it.privacy === LibraryPrivacy.TEAM && it.coachId === user?.id),
      );

      const publicTemplates = filtered?.filter(
        (it) => it.privacy === LibraryPrivacy.PUBLIC,
      );

      const teamTemplates = filtered?.filter(
        (it) => it.privacy === LibraryPrivacy.TEAM && it.coachId !== user?.id,
      );

      return { privateTemplates, publicTemplates, teamTemplates };
    }, [templates, search]);

  const categories = React.useMemo(() => {
    const categories = uniq(publicTemplates?.map(({ category }) => category));
    categories.sort();

    return categories;
  }, [publicTemplates]);

  const teamCoachesCategories = React.useMemo(() => {
    const categories = uniq(
      teamTemplates?.map(({ coachId }) => String(coachId)),
    );
    categories.sort();

    return categories;
  }, [teamTemplates]);

  const coachIdToDisplayName = React.useMemo(() => {
    return (
      teamTemplates?.reduce(
        (acc, template) => {
          acc[String(template.coachId)] = template.coachDisplayName;
          return acc;
        },
        {} as Record<string, string>,
      ) || {}
    );
  }, [teamTemplates]);

  const handleSearchChange = React.useCallback(
    (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
      setSearch(event.currentTarget.value);
    },
    [],
  );

  const handleCategoryClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const {
        dataset: { category },
      } = event.currentTarget;

      setExpanded(
        expanded.includes(category)
          ? expanded.filter((it) => it !== category)
          : [...expanded, category],
      );
    },
    [expanded],
  );

  const handleTemplateClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const {
        dataset: { templateId },
      } = event.currentTarget;
      if (onSelect) {
        onSelect(event, templateId);
      }
    },
    [onSelect],
  );

  const handleRemoveTemplate = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();

      const {
        dataset: { templateId },
      } = event.currentTarget;

      removeTemplate(templateId);
    },
    [removeTemplate, showToastAlert],
  );

  return (
    <Box className={clsx(s.root, className)} {...other}>
      <TextField
        className={s.search}
        slotProps={{
          input: {
            className: s.searchInput,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
        value={search}
        onChange={handleSearchChange as any}
        placeholder="Search"
        variant="outlined"
        fullWidth
      />
      <Box className={clsx(s.body)}>
        <Typography className={s.title} variant="subtitle1">
          My templates
        </Typography>
        {isLoadingTemplatesList ? (
          <SkeletonList />
        ) : privateTemplates?.length > 0 ? (
          <>
            <List component="div" className={s.myTemplates} disablePadding>
              {privateTemplates.map(({ id, title }) => (
                <ListItemButton
                  className={s.template}
                  key={id}
                  onClick={handleTemplateClick}
                  data-template-id={id}
                >
                  <ListItemText classes={{ primary: s.text }}>
                    {title}
                    <IconButton
                      className={s.removeButton}
                      onClick={handleRemoveTemplate}
                      data-template-id={id}
                      size="large"
                    >
                      <RemoveIcon />
                    </IconButton>
                  </ListItemText>
                </ListItemButton>
              ))}
            </List>
          </>
        ) : (
          <Typography variant="body2">
            You don't have any private templates.
          </Typography>
        )}

        {user?.isTeamAccount && (
          <TemplatesCategoryList
            title="Team templates"
            templates={teamTemplates}
            categories={teamCoachesCategories}
            getCategoryValue={(it) => String(it.coachId)}
            getCategoryDisplayName={(categoryId) =>
              coachIdToDisplayName[categoryId]
            }
            isLoading={isLoadingTemplatesList}
            emptyMessage="No team templates."
            expanded={expanded}
            onCategoryClick={handleCategoryClick}
            onTemplateClick={handleTemplateClick}
            classes={s}
          />
        )}

        <TemplatesCategoryList
          title="Public templates"
          templates={publicTemplates}
          categories={categories}
          getCategoryValue={(it) => it.category}
          isLoading={isLoadingTemplatesList}
          emptyMessage="No public templates."
          expanded={expanded}
          onCategoryClick={handleCategoryClick}
          onTemplateClick={handleTemplateClick}
          classes={s}
        />
      </Box>
    </Box>
  );
}
